import { Controller } from '@hotwired/stimulus';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default class extends Controller {
  static values = {
    stats: Object,
  };

  static targets = [
    'audioChart',
    'videoChart',
    'audioQualityChart',
    'videoQualityChart',
    'audioToggle',
    'videoToggle',
    'audioParticipantSelect',
    'videoParticipantSelect',
    'overallQuality',
    'audioQuality',
    'videoQuality',
    'avgLatency',
    'packetLoss',
    'videoResolution',
    'collapsibleContent',
    'chevronIcon',
  ];

  connect() {
    this.audioChart = null;
    this.videoChart = null;
    this.audioQualityChart = null;
    this.videoQualityChart = null;
    this.currentAudioView = 'input';
    this.currentVideoView = 'input';
    this.selectedAudioParticipant = 'all'; // Default to show all participants for audio
    this.selectedVideoParticipant = 'all'; // Default to show all participants for video
    this.isExpanded = false;

    this.processMultiParticipantStats();
    this.populateParticipantDropdowns();
    this.updateOverviewMetrics();
    this.initializeCharts();
  }

  disconnect() {
    if (this.audioChart) this.audioChart.destroy();
    if (this.videoChart) this.videoChart.destroy();
    if (this.audioQualityChart) this.audioQualityChart.destroy();
    if (this.videoQualityChart) this.videoQualityChart.destroy();
  }

  processMultiParticipantStats() {
    const statsData = this.statsValue;
    const participants = Object.keys(statsData);

    // Initialize data structures
    this.participantData = {};
    this.combinedTimestamps = new Set();

    // Process each participant's data
    participants.forEach((participantId) => {
      const stats = statsData[participantId];

      this.participantData[participantId] = {
        audio: {
          input: this.extractTimeSeriesData(stats.audio_input),
          output: this.extractTimeSeriesData(stats.audio_output),
        },
        video: {
          input: this.extractTimeSeriesData(stats.video_input),
          output: this.extractTimeSeriesData(stats.video_output),
        },
        quality: {
          audio: this.extractQualityData(stats.audio_quality),
          video: this.extractQualityData(stats.video_quality),
        },
      };

      // Collect all unique timestamps
      Object.values(this.participantData[participantId].audio).forEach((data) => {
        data.timestamps.forEach((ts) => this.combinedTimestamps.add(ts));
      });
      Object.values(this.participantData[participantId].video).forEach((data) => {
        data.timestamps.forEach((ts) => this.combinedTimestamps.add(ts));
      });
    });

    // Convert to sorted array
    this.allTimestamps = Array.from(this.combinedTimestamps).sort();
  }

  extractTimeSeriesData(data) {
    if (!data)
      return {
        timestamps: [],
        bitrate: [],
        latency: [],
        jitter: [],
        avg_loss: [],
      };

    const timestamps = [];
    const bitrate = [];
    const latency = [];
    const jitter = [];
    const avg_loss = [];

    Object.entries(data).forEach(([timestamp, metrics]) => {
      if (metrics && Object.keys(metrics).length > 0) {
        timestamps.push(this.formatTimestamp(timestamp));
        bitrate.push(metrics.bitrate || 0);
        latency.push(metrics.latency || 0);
        jitter.push(metrics.jitter || 0);
        avg_loss.push(metrics.avg_loss || 0);
      }
    });

    return { timestamps, bitrate, latency, jitter, avg_loss };
  }

  extractQualityData(data) {
    if (!data)
      return {
        timestamps: [],
        scores: [],
        average: 0,
        latest: 0,
      };

    const timestamps = [];
    const scores = [];
    const qualityScores = [];

    Object.entries(data).forEach(([timestamp, entry]) => {
      if (entry && entry.quality_score) {
        timestamps.push(this.formatTimestamp(timestamp));
        scores.push(entry.quality_score);
        qualityScores.push(entry.quality_score);
      }
    });

    if (qualityScores.length === 0) {
      return { timestamps: [], scores: [], average: 0, latest: 0 };
    }

    const average = qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length;
    const latest = qualityScores[qualityScores.length - 1];

    return {
      timestamps,
      scores,
      average: average.toFixed(1),
      latest: latest.toFixed(1),
    };
  }

  formatTimestamp(timestamp) {
    const date = new Date(parseFloat(timestamp));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  updateOverviewMetrics() {
    // For overview metrics, use all participants
    const participants = Object.keys(this.participantData);
    let totalAudioQuality = 0;
    let totalVideoQuality = 0;
    const allLatencies = [];
    const allLossValues = [];

    participants.forEach((participantId) => {
      const data = this.participantData[participantId];

      // Collect quality scores
      const audioQuality = parseFloat(data.quality.audio.average) || 0;
      const videoQuality = parseFloat(data.quality.video.average) || 0;
      totalAudioQuality += audioQuality;
      totalVideoQuality += videoQuality;

      // Collect latencies
      allLatencies.push(...data.audio.input.latency, ...data.audio.output.latency);
      allLatencies.push(...data.video.input.latency, ...data.video.output.latency);

      // Collect packet loss
      allLossValues.push(...data.audio.input.avg_loss, ...data.audio.output.avg_loss);
      allLossValues.push(...data.video.input.avg_loss, ...data.video.output.avg_loss);
    });

    // Calculate averages
    const avgAudioQuality = participants.length > 0 ? (totalAudioQuality / participants.length).toFixed(1) : '0';
    const avgVideoQuality = participants.length > 0 ? (totalVideoQuality / participants.length).toFixed(1) : '0';
    const overallQuality =
      participants.length > 0 ? ((totalAudioQuality + totalVideoQuality) / (participants.length * 2)).toFixed(1) : '0';

    const validLatencies = allLatencies.filter((val) => val > 0);
    const avgLatency =
      validLatencies.length > 0
        ? Math.round(validLatencies.reduce((sum, val) => sum + val, 0) / validLatencies.length)
        : 0;

    const validLoss = allLossValues.filter((val) => val >= 0);
    const avgPacketLoss =
      validLoss.length > 0 ? ((validLoss.reduce((sum, val) => sum + val, 0) / validLoss.length) * 100).toFixed(2) : 0;

    // Update UI
    if (this.hasOverallQualityTarget) {
      this.overallQualityTarget.textContent = overallQuality;
    }
    if (this.hasAudioQualityTarget) {
      this.audioQualityTarget.textContent = avgAudioQuality;
    }
    if (this.hasVideoQualityTarget) {
      this.videoQualityTarget.textContent = avgVideoQuality;
    }
    if (this.hasAvgLatencyTarget) {
      this.avgLatencyTarget.textContent = avgLatency;
    }
    if (this.hasPacketLossTarget) {
      this.packetLossTarget.textContent = `${avgPacketLoss}%`;
    }
  }

  populateParticipantDropdowns() {
    if (this.hasAudioParticipantSelectTarget) {
      this.populateDropdown(this.audioParticipantSelectTarget, 'audio');
    }
    if (this.hasVideoParticipantSelectTarget) {
      this.populateDropdown(this.videoParticipantSelectTarget, 'video');
    }
  }

  populateDropdown(selectElement, type) {
    if (!selectElement) return;

    const participants = Object.keys(this.participantData);

    // Clear existing options
    selectElement.innerHTML = '';

    // Add "All Participants" option
    const allOption = document.createElement('option');
    allOption.value = 'all';
    allOption.textContent = `All Participants (${type})`;
    allOption.selected = true;
    selectElement.appendChild(allOption);

    // Add individual participant options
    participants.forEach((participantId, index) => {
      const option = document.createElement('option');
      option.value = participantId;
      option.textContent = participantId;
      selectElement.appendChild(option);
    });
  }

  getFilteredAudioParticipants() {
    if (this.selectedAudioParticipant === 'all') {
      return Object.keys(this.participantData);
    }
    return [this.selectedAudioParticipant];
  }

  getFilteredVideoParticipants() {
    if (this.selectedVideoParticipant === 'all') {
      return Object.keys(this.participantData);
    }
    return [this.selectedVideoParticipant];
  }

  // Fallback method for backwards compatibility
  getFilteredParticipants() {
    return Object.keys(this.participantData);
  }

  onAudioParticipantChange(event) {
    this.selectedAudioParticipant = event.target.value;
    this.updateAudioCharts();
  }

  onVideoParticipantChange(event) {
    this.selectedVideoParticipant = event.target.value;
    this.updateVideoCharts();
  }

  initializeCharts() {
    // Make sure participant data exists before initializing charts
    if (!this.participantData || Object.keys(this.participantData).length === 0) {
      console.warn('No participant data available for charts');
      return;
    }

    this.initAudioChart();
    this.initVideoChart();
    this.initAudioQualityChart();
    this.initVideoQualityChart();
  }

  initAudioChart() {
    if (!this.hasAudioChartTarget) return;

    const ctx = this.audioChartTarget.getContext('2d');

    this.audioChart = new Chart(ctx, {
      type: 'line',
      data: this.getAudioChartData('input'),
      options: this.getChartOptions('Audio Performance Metrics'),
    });
  }

  initVideoChart() {
    if (!this.hasVideoChartTarget) return;

    const ctx = this.videoChartTarget.getContext('2d');

    this.videoChart = new Chart(ctx, {
      type: 'line',
      data: this.getVideoChartData('input'),
      options: this.getChartOptions('Video Performance Metrics'),
    });
  }

  initAudioQualityChart() {
    if (!this.hasAudioQualityChartTarget) return;

    const ctx = this.audioQualityChartTarget.getContext('2d');

    this.audioQualityChart = new Chart(ctx, {
      type: 'line',
      data: this.getAudioQualityChartData(),
      options: this.getQualityChartOptions('Audio Quality Scores'),
    });
  }

  initVideoQualityChart() {
    if (!this.hasVideoQualityChartTarget) return;

    const ctx = this.videoQualityChartTarget.getContext('2d');

    this.videoQualityChart = new Chart(ctx, {
      type: 'line',
      data: this.getVideoQualityChartData(),
      options: this.getQualityChartOptions('Video Quality Scores'),
    });
  }

  getAudioChartData(view) {
    if (!this.participantData) {
      return { labels: [], datasets: [] };
    }

    const datasets = [];
    const participants = this.getFilteredAudioParticipants();
    const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545']; // Blue, Green, Yellow, Red
    const metrics = ['bitrate', 'jitter', 'latency', 'avg_loss'];
    const metricColors = {
      bitrate: '#007bff',
      jitter: '#28a745',
      latency: '#ffc107',
      avg_loss: '#dc3545',
    };

    participants.forEach((participantId, participantIndex) => {
      const data = this.participantData[participantId];

      if (!data || !data.audio) return;

      metrics.forEach((metric) => {
        if (view === 'input' || view === 'both') {
          datasets.push({
            label: `${metric} - ${participantId} (Input)`,
            data: data.audio.input[metric] || [],
            borderColor: metricColors[metric],
            backgroundColor: `${metricColors[metric]}20`,
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            borderDash: participantIndex > 0 ? [5, 5] : [],
          });
        }

        if (view === 'output' || view === 'both') {
          datasets.push({
            label: `${metric} - ${participantId} (Output)`,
            data: data.audio.output[metric] || [],
            borderColor: metricColors[metric],
            backgroundColor: `${metricColors[metric]}20`,
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            borderDash: participantIndex > 0 ? [10, 5] : [5, 5],
          });
        }
      });
    });

    return {
      labels: this.allTimestamps || [],
      datasets,
    };
  }

  getVideoChartData(view) {
    if (!this.participantData) {
      return { labels: [], datasets: [] };
    }

    const datasets = [];
    const participants = this.getFilteredVideoParticipants();
    const metrics = ['bitrate', 'jitter', 'latency', 'avg_loss'];
    const metricColors = {
      bitrate: '#6f42c1',
      jitter: '#20c997',
      latency: '#fd7e14',
      avg_loss: '#e83e8c',
    };

    participants.forEach((participantId, participantIndex) => {
      const data = this.participantData[participantId];

      if (!data || !data.video) return;

      metrics.forEach((metric) => {
        if (view === 'input' || view === 'both') {
          datasets.push({
            label: `${metric} - ${participantId} (Input)`,
            data: data.video.input[metric] || [],
            borderColor: metricColors[metric],
            backgroundColor: `${metricColors[metric]}20`,
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            borderDash: participantIndex > 0 ? [5, 5] : [],
          });
        }

        if (view === 'output' || view === 'both') {
          datasets.push({
            label: `${metric} - ${participantId} (Output)`,
            data: data.video.output[metric] || [],
            borderColor: metricColors[metric],
            backgroundColor: `${metricColors[metric]}20`,
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            borderDash: participantIndex > 0 ? [10, 5] : [5, 5],
          });
        }
      });
    });

    return {
      labels: this.allTimestamps || [],
      datasets,
    };
  }

  getAudioQualityChartData() {
    if (!this.participantData) {
      return { labels: [], datasets: [] };
    }

    const datasets = [];
    const participants = this.getFilteredAudioParticipants();
    const colors = ['#17a2b8', '#6610f2'];

    participants.forEach((participantId, participantIndex) => {
      const data = this.participantData[participantId];

      if (!data || !data.quality || !data.quality.audio) return;

      const qualityData = data.quality.audio;

      datasets.push({
        label: `Audio Quality - ${participantId}`,
        data: qualityData.scores || [],
        borderColor: colors[participantIndex % colors.length],
        backgroundColor: `${colors[participantIndex % colors.length]}20`,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointRadius: 4,
      });
    });

    return {
      labels: this.allTimestamps || [],
      datasets,
    };
  }

  getVideoQualityChartData() {
    if (!this.participantData) {
      return { labels: [], datasets: [] };
    }

    const datasets = [];
    const participants = this.getFilteredVideoParticipants();
    const colors = ['#795548', '#607d8b'];

    participants.forEach((participantId, participantIndex) => {
      const data = this.participantData[participantId];

      if (!data || !data.quality || !data.quality.video) return;

      const qualityData = data.quality.video;

      datasets.push({
        label: `Video Quality - ${participantId}`,
        data: qualityData.scores || [],
        borderColor: colors[participantIndex % colors.length],
        backgroundColor: `${colors[participantIndex % colors.length]}20`,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointRadius: 4,
      });
    });

    return {
      labels: this.allTimestamps || [],
      datasets,
    };
  }

  getChartOptions(title) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        title: {
          display: true,
          text: title,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 6,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: '#ddd',
          borderWidth: 1,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Value',
          },
          grid: {
            color: 'rgba(0,0,0,0.1)',
          },
        },
        x: {
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0,0,0,0.1)',
          },
        },
      },
    };
  }

  getQualityChartOptions(title) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        title: {
          display: true,
          text: title,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        legend: {
          position: 'bottom',
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: '#ddd',
          borderWidth: 1,
        },
      },
      scales: {
        y: {
          min: 0,
          max: 5,
          title: {
            display: true,
            text: 'Quality Score (0-5)',
          },
          grid: {
            color: 'rgba(0,0,0,0.1)',
          },
        },
        x: {
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0,0,0,0.1)',
          },
        },
      },
    };
  }

  toggleContent() {
    this.isExpanded = !this.isExpanded;

    if (this.hasCollapsibleContentTarget) {
      if (this.isExpanded) {
        this.collapsibleContentTarget.style.display = 'block';
      } else {
        this.collapsibleContentTarget.style.display = 'none';
      }
    }

    if (this.hasChevronIconTarget) {
      if (this.isExpanded) {
        this.chevronIconTarget.classList.remove('bi-chevron-down');
        this.chevronIconTarget.classList.add('bi-chevron-up');
      } else {
        this.chevronIconTarget.classList.remove('bi-chevron-up');
        this.chevronIconTarget.classList.add('bi-chevron-down');
      }
    }

    if (this.isExpanded) {
      setTimeout(() => {
        if (this.audioChart) this.audioChart.resize();
        if (this.videoChart) this.videoChart.resize();
        if (this.audioQualityChart) this.audioQualityChart.resize();
        if (this.videoQualityChart) this.videoQualityChart.resize();
      }, 200);
    }
  }

  toggleAudioView(event) {
    const { view } = event.target.dataset;
    this.currentAudioView = view;

    this.audioToggleTargets.forEach((btn) => {
      btn.classList.remove('active');
      if (btn.dataset.view === view) {
        btn.classList.add('active');
      }
    });

    this.audioChart.data = this.getAudioChartData(view);
    this.audioChart.update();
  }

  toggleVideoView(event) {
    const { view } = event.target.dataset;
    this.currentVideoView = view;

    this.videoToggleTargets.forEach((btn) => {
      btn.classList.remove('active');
      if (btn.dataset.view === view) {
        btn.classList.add('active');
      }
    });

    this.videoChart.data = this.getVideoChartData(view);
    this.videoChart.update();
  }

  updateAudioCharts() {
    if (this.audioChart) {
      this.audioChart.data = this.getAudioChartData(this.currentAudioView);
      this.audioChart.update();
    }

    if (this.audioQualityChart) {
      this.audioQualityChart.data = this.getAudioQualityChartData();
      this.audioQualityChart.update();
    }
  }

  updateVideoCharts() {
    if (this.videoChart) {
      this.videoChart.data = this.getVideoChartData(this.currentVideoView);
      this.videoChart.update();
    }

    if (this.videoQualityChart) {
      this.videoQualityChart.data = this.getVideoQualityChartData();
      this.videoQualityChart.update();
    }
  }

  updateAllCharts() {
    this.updateAudioCharts();
    this.updateVideoCharts();
  }
}
